import React, { useMemo, memo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Modal,
  ScrollView,
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { REPORT_STATUS_STYLES, REPORT_REASON_STYLES } from '@/constants/residents/corkBoardConstants';
import type { CorkBoardReport } from '@/types/residents/corkBoard';

interface ReportDetailModalProps {
  visible: boolean;
  report: CorkBoardReport | null;
  isMutating: boolean;
  onRemove: (report: CorkBoardReport) => void;
  onDismiss: (report: CorkBoardReport) => void;
  onWarn: (report: CorkBoardReport) => void;
  onClose: () => void;
}

export const ReportDetailModal: React.FC<ReportDetailModalProps> = memo(
  ({ visible, report, isMutating, onRemove, onDismiss, onWarn, onClose }) => {
    const isActionable = report
      ? report.status === 'pending' || report.status === 'reviewed'
      : false;

    const formattedDate = useMemo(() => {
      if (!report) return '';
      return new Date(report.createdAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }, [report]);

    const formattedReviewedAt = useMemo(() => {
      if (!report?.reviewedAt) return null;
      return new Date(report.reviewedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }, [report]);

    if (!report) return null;

    const statusStyle = REPORT_STATUS_STYLES[report.status] ?? REPORT_STATUS_STYLES.pending;
    const reasonStyle = REPORT_REASON_STYLES[report.reason] ?? REPORT_REASON_STYLES.other;

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={isMutating ? undefined : onClose}>
        <Pressable style={styles.overlay} onPress={isMutating ? undefined : onClose}>
          <Pressable style={styles.modalContainer} onPress={(e) => e.stopPropagation()}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.headerTitle}>Report Details</Text>
              <TouchableOpacity
                onPress={onClose}
                disabled={isMutating}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <View style={isMutating ? styles.closeButtonDisabled : undefined}>
                  <SVG_COMPONENTS.CANCEL_X width={18} height={18} color={ADMIN_COLORS.TEXT.SECONDARY} />
                </View>
              </TouchableOpacity>
            </View>

            {/* Body */}
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              {/* Status + Type */}
              <View style={styles.badgeRow}>
                <View style={[styles.badge, report.contentType === 'thread' ? styles.threadBadge : styles.replyBadge]}>
                  <Text style={styles.badgeText}>
                    {report.contentType === 'thread' ? 'Thread' : 'Reply'}
                  </Text>
                </View>
                <View style={[styles.badge, { backgroundColor: statusStyle.bg }]}>
                  <Text style={[styles.statusText, { color: statusStyle.text }]}>
                    {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                  </Text>
                </View>
                <View style={[styles.badge, { backgroundColor: reasonStyle.bg }]}>
                  <Text style={[styles.statusText, { color: reasonStyle.text }]}>
                    {report.reason.charAt(0).toUpperCase() + report.reason.slice(1)}
                  </Text>
                </View>
              </View>

              {/* Reported Content */}
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Reported Content</Text>
                <View style={styles.contentBox}>
                  <Text style={styles.contentText}>
                    {report.contentPreview || '(content unavailable)'}
                  </Text>
                </View>
              </View>

              {/* Reporter */}
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Reporter</Text>
                <Text style={styles.sectionValue}>{report.reporter.name}</Text>
              </View>

              {/* Reason Details */}
              {report.details && (
                <View style={styles.section}>
                  <Text style={styles.sectionLabel}>Additional Details</Text>
                  <View style={styles.contentBox}>
                    <Text style={styles.detailsText}>{report.details}</Text>
                  </View>
                </View>
              )}

              {/* Dates */}
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Reported On</Text>
                <Text style={styles.sectionValue}>{formattedDate}</Text>
              </View>

              {/* Reviewer info */}
              {report.reviewedBy && (
                <View style={styles.section}>
                  <Text style={styles.sectionLabel}>Reviewed By</Text>
                  <Text style={styles.sectionValue}>
                    {report.reviewedBy.name}
                    {formattedReviewedAt ? ` on ${formattedReviewedAt}` : ''}
                  </Text>
                </View>
              )}
            </ScrollView>

            {/* Footer */}
            <View style={styles.footer}>
              <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={isMutating}>
                <Text style={styles.cancelButtonText}>Close</Text>
              </TouchableOpacity>

              {isActionable && (
                <>
                  <TouchableOpacity
                    style={[styles.warnButton, isMutating && styles.buttonDisabled]}
                    onPress={() => onWarn(report)}
                    disabled={isMutating}
                  >
                    <Text style={styles.warnButtonText}>{isMutating ? 'Processing...' : 'Warn User'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.dismissButton, isMutating && styles.buttonDisabled]}
                    onPress={() => onDismiss(report)}
                    disabled={isMutating}
                  >
                    <Text style={styles.dismissButtonText}>{isMutating ? 'Processing...' : 'Dismiss'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.removeButton, isMutating && styles.buttonDisabled]}
                    onPress={() => onRemove(report)}
                    disabled={isMutating}
                  >
                    <Text style={styles.removeButtonText}>{isMutating ? 'Processing...' : 'Remove Content'}</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    );
  },
);

ReportDetailModal.displayName = 'ReportDetailModal';

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 520,
    maxHeight: '85%',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  closeButtonDisabled: {
    opacity: 0.3,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: 20,
    gap: 16,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  threadBadge: {
    backgroundColor: '#3B82F6',
  },
  replyBadge: {
    backgroundColor: '#8B5CF6',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  section: {
    gap: 4,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionValue: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  contentBox: {
    backgroundColor: ADMIN_COLORS.INPUT.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    padding: 12,
  },
  contentText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    lineHeight: 20,
  },
  detailsText: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    lineHeight: 18,
    fontStyle: 'italic',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    flexWrap: 'wrap',
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER_SECONDARY,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  warnButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  warnButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#92400E',
  },
  dismissButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER_SECONDARY,
  },
  dismissButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  removeButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#EF4444',
  },
  removeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#991B1B',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});
